import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-8f238a90"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "mx-auto" }
const _hoisted_2 = { class: "mb-8" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", { class: "text-3xl font-semibold text-gray-900 work-sans" }, "Grammar List", -1))
const _hoisted_4 = { class: "student-name-heading" }
const _hoisted_5 = {
  key: 0,
  class: "flex justify-center items-center min-h-[300px]"
}
const _hoisted_6 = {
  key: 1,
  class: "flex justify-center items-center min-h-[300px]"
}
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "text-xl text-gray-600" }, "No Lingo data available for this student yet.", -1))
const _hoisted_8 = [
  _hoisted_7
]
const _hoisted_9 = { key: 2 }
const _hoisted_10 = { class: "dashboard-container" }
const _hoisted_11 = { class: "sidebar" }
const _hoisted_12 = { class: "sidebar__body" }
const _hoisted_13 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "poppins" }, "Overview", -1))
const _hoisted_14 = { class: "arch-section" }
const _hoisted_15 = { class: "main-content" }
const _hoisted_16 = { class: "data-tables" }
const _hoisted_17 = { class: "table-container" }
const _hoisted_18 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "table-heading" }, [
  /*#__PURE__*/_createElementVNode("h3", null, "Grammar List")
], -1))
const _hoisted_19 = {
  key: 1,
  class: "kc-name-plain"
}
const _hoisted_20 = ["onClick"]
const _hoisted_21 = { class: "status-text status-cell" }

import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import ProgressStatsCard from "@/modules/main/components/manage/ProgressStatsCardComponent.vue";
import LingoProgressCircle from "@/modules/main/components/manage/LingoProgressCircleComponent.vue";
import LingoTooltipIcon from "@/modules/main/components/manage/LingoTooltipIconComponent.vue";
import LingoDataTable from "@/modules/main/components/manage/LingoDataTableComponent.vue";
import i18n from "@/plugins/i18n";
import { useLingoStore } from "@/stores/lingoStore";
import { useLearnerStore } from '@/stores/learnerStore';
import getKcStatusTooltipFn from "@/modules/main/utils/kc";
import type { KnowledgeComponent } from "@/interfaces/lingo.interfaces";
import LevelDescriptionModal from "@/components/manage/LevelDescriptionModal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'LingoStudentGrammarListView',
  setup(__props) {

const route = useRoute();
const lingoStore = useLingoStore();
const learnerStore = useLearnerStore();

//breadcrumbs
const pages = computed(() => {
  const base = [{ name: "Lingo Data", path: "/main/lingo-data" }];
  base.push({
    name: "Data for " + displayName.value,
    path: "/main/lingo-data/student/" + studentId.value
  });
  base.push({
    name: `Grammar List`,
    path: "#"
  });
  return base;
});

// Mirrors the learner app's grammar list exactly (NLM-Test #93): no Topic column,
// no example line under the KC, "Level N" spelled out and clickable.
const cols = [
  { key: "name", label: "Knowledge Component", sortable: true },
  { key: "level", label: "Level", sortable: true, width: "160px" },
  { key: "status", label: "Status", sortable: true, width: "260px" }
];

const STATUS_LABELS: Record<string, string> = {
  not_yet_seen: "Not yet seen",
  seen: "Seen",
  learning: "Learning",
  practicing: "Reinforcing",
  mastered_prior_learning: "Mastered (Prior Learning)",
  mastered_new_learning: "Mastered (Gap Filled)",
};

const STATUS_RANK: Record<string, number> = {
  not_yet_seen: 0,
  seen: 1,
  learning: 2,
  reinforcing: 3,
  practicing: 3,
  mastered_prior_learning: 4,
  mastered_new_learning: 5
};

const studentId = computed(() => route.params.studentId as string);
const selectedStudent = computed(() => learnerStore.getLearnerById(studentId.value) || null);

function capitalise(str: string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const displayName = computed(() => {
  const student = selectedStudent.value;
  if (!student) return 'Unknown Student';
  return `${capitalise(student.firstName)} ${capitalise(student.lastName)}`.trim();
});

const studentLingoData = computed(() =>
  lingoStore.getLingoDataByStudentId(studentId.value)
);

// Learner grammar list uses ALL grammar KCs from allKCs (not just the current
// activity subset), de-duplicated by strapiWordId keeping the highest status —
// matches the learner app's grammar list behaviour.
const rawGrammarKCs = computed<KnowledgeComponent[]>(() => {
  const all = studentLingoData.value?.allKCs;
  return Array.isArray(all) ? all.filter(kc => kc && kc.type === "grammar") : [];
});

const dedupedGrammarKCs = computed<KnowledgeComponent[]>(() => {
  const byKey = new Map<string, KnowledgeComponent>();
  for (const kc of rawGrammarKCs.value) {
    const key = String(kc.strapiWordId ?? (kc as any).id ?? kc._id ?? kc.name ?? kc.word ?? "");
    const existing = byKey.get(key);
    if (!existing || (STATUS_RANK[kc.status] ?? -1) > (STATUS_RANK[existing.status] ?? -1)) {
      byKey.set(key, kc);
    }
  }
  return [...byKey.values()];
});

const grammarKCs = computed(() => {
  return dedupedGrammarKCs.value.map((kc, index) => ({
    ...kc,
    __rowKey: `${String(kc.strapiWordId ?? kc._id ?? kc.name ?? index)}-${index}`
  }));
});

const statusCounts = computed(() => {
  const initial: Record<string, number> = {
    not_yet_seen: 0,
    seen: 0,
    learning: 0,
    practicing: 0,
    mastered_prior_learning: 0,
    mastered_new_learning: 0
  };

  return dedupedGrammarKCs.value.reduce((counts, kc: any) => {
    const s = kc && (kc.status === "reinforcing" ? "practicing" : kc.status);
    if (s && Object.prototype.hasOwnProperty.call(counts, s)) {
      counts[s] += 1;
    }
    return counts;
  }, { ...initial });
});

const totalKcs = computed(() => dedupedGrammarKCs.value.length);

const masteredCount = computed(() => (statusCounts.value.mastered_prior_learning || 0) + (statusCounts.value.mastered_new_learning || 0));

const progressStats = computed(() => {
  const total = totalKcs.value || 1;
  return [
    {
      status: "not_yet_seen",
      label: "Not yet seen",
      count: statusCounts.value.not_yet_seen,
      percentage: (statusCounts.value.not_yet_seen / total) * 100,
      color: "#667085",
      tooltip: i18n.global.t("lingo_v2.tooltips.kc_arch_progress_status.not_yet_seen").toString()
    },
    {
      status: "seen",
      label: "Seen",
      count: statusCounts.value.seen,
      percentage: (statusCounts.value.seen / total) * 100,
      color: "#1B7442",
      tooltip: i18n.global.t("lingo_v2.tooltips.kc_arch_progress_status.seen").toString()
    },
    {
      status: "learning",
      label: "Learning",
      count: statusCounts.value.learning,
      percentage: (statusCounts.value.learning / total) * 100,
      color: "#1B7442",
      tooltip: i18n.global.t("lingo_v2.tooltips.kc_arch_progress_status.learning").toString()
    },
    {
      status: "practicing",
      label: "Reinforcing",
      count: statusCounts.value.practicing,
      percentage: (statusCounts.value.practicing / total) * 100,
      color: "#1B7442",
      tooltip: i18n.global.t("lingo_v2.tooltips.kc_arch_progress_status.reinforcing").toString()
    },
    {
      status: "mastered",
      label: "Mastered",
      count: masteredCount.value,
      percentage: (masteredCount.value / total) * 100,
      color: "#EAAA08",
      tooltip: i18n.global.t("lingo_v2.tooltips.kc_arch_progress_status.mastered").toString()
    }
  ];
});

// Fall back to computing mastery from the deduped list when the API does not
// (yet) provide grammarMasteredCount/grammarTotalCount for this student.
const grammarMasteredCountResolved = computed(() => {
  const val = studentLingoData.value?.grammarMasteredCount;
  return typeof val === "number" ? val : masteredCount.value;
});

const grammarTotalCountResolved = computed(() => {
  const val = studentLingoData.value?.grammarTotalCount;
  return typeof val === "number" ? val : totalKcs.value;
});

const grammarPercentage = computed(() => {
  const mastered = grammarMasteredCountResolved.value || 0;
  const total = grammarTotalCountResolved.value || 0;
  if (total === 0) return 0;
  return Math.round((mastered / total) * 100);
});

const grammarMasteryDisplay = computed(() => {
  const mastered = grammarMasteredCountResolved.value || 0;
  const total = grammarTotalCountResolved.value || 0;
  return `${mastered}/${total}`;
});

function getKcData(status: string | null | undefined, dataType: "label" | string) {
  if (status == null) return null;
  const normalized = String(status);
  if (normalized === "mastered_prior_learning") return "Mastered (Prior Learning)";
  if (normalized === "mastered_new_learning") return "Mastered (Gap Filled)";

  const statsArray = Array.isArray((progressStats as any)?.value) ? (progressStats as any).value : (progressStats as any) || [];
  const entry = statsArray.find((e: any) => e.status === normalized || (normalized === "reinforcing" && e.status === "practicing"));
  if (!entry) return null;
  if (entry[dataType] === undefined || entry[dataType] === null) return null;
  return entry[dataType];
}

function appendItemLevel(level: number) {
  const levelNum = Number(level);
  if (levelNum === 7) {
    return levelNum + "+";
  }
  return level;
}

// Level description modal, matching the learner app's clickable level (NLM-Test #93).
const selectedLevel = ref<number | string | null>(null);

function openLevelModalFor(item: { level?: number | null }) {
  const level = item && item.level != null ? appendItemLevel(item.level) : null;
  selectedLevel.value = level;
}

onMounted(async () => {
  if (!studentLingoData.value) {
    await lingoStore.fetchStudentLingoData({
      studentIds: [studentId.value]
    });
  }
});

return (_ctx: any,_cache: any) => {
  const _component_page_title = _resolveComponent("page-title")!
  const _component_spinner_loader = _resolveComponent("spinner-loader")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_page_title, {
      pages: pages.value,
      hideFilters: true,
      highlightLast: true
    }, null, 8, ["pages"]),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _hoisted_3,
        _createElementVNode("span", _hoisted_4, _toDisplayString(displayName.value), 1)
      ]),
      (_unref(lingoStore).loader?.fetchStudentLingoData)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createVNode(_component_spinner_loader, { color: 'text-blue-600' })
          ]))
        : (!studentLingoData.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, _hoisted_8))
          : (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("aside", _hoisted_11, [
                  _createElementVNode("div", _hoisted_12, [
                    _hoisted_13,
                    _createElementVNode("div", _hoisted_14, [
                      _createVNode(LingoProgressCircle, {
                        size: "lgBorder",
                        value: grammarPercentage.value,
                        shape: "half",
                        color: "#fbbf24",
                        caption: "Mastered",
                        "display-value": grammarMasteryDisplay.value,
                        "show-percent-sign": false
                      }, null, 8, ["value", "display-value"])
                    ]),
                    _createVNode(ProgressStatsCard, { stats: progressStats.value }, null, 8, ["stats"])
                  ])
                ]),
                _createElementVNode("main", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("div", _hoisted_17, [
                      _hoisted_18,
                      _createVNode(LingoDataTable, {
                        items: grammarKCs.value,
                        columns: cols,
                        "row-key": "__rowKey",
                        class: "my-grammar-data-table"
                      }, {
                        "cell-name": _withCtx(({ item }) => [
                          (item.strapiWordId != null)
                            ? (_openBlock(), _createBlock(_component_router_link, {
                                key: 0,
                                class: "text-blue open-modal",
                                to: `/main/lingo-grammar-kc/${studentId.value}/${encodeURIComponent(String(item.strapiWordId))}`,
                                "aria-label": "Open grammar knowledge component details"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(item.name || "-"), 1)
                                ]),
                                _: 2
                              }, 1032, ["to"]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_19, _toDisplayString(item.name || "-"), 1))
                        ]),
                        "cell-level": _withCtx(({ item }) => [
                          _createElementVNode("span", {
                            class: "text-blue open-modal",
                            onClick: ($event: any) => (openLevelModalFor(item))
                          }, " Level " + _toDisplayString(item.level != null ? appendItemLevel(item.level) : "-"), 9, _hoisted_20)
                        ]),
                        "cell-status": _withCtx(({ item }) => [
                          _createElementVNode("span", _hoisted_21, [
                            _createElementVNode("span", null, _toDisplayString(getKcData(item.status, "label")), 1),
                            (_unref(getKcStatusTooltipFn)(item.status))
                              ? (_openBlock(), _createBlock(LingoTooltipIcon, {
                                  key: 0,
                                  text: _unref(getKcStatusTooltipFn)(item.status),
                                  ariaLabel: STATUS_LABELS[item.status],
                                  placement: "top",
                                  class: "margin-top-1"
                                }, null, 8, ["text", "ariaLabel"]))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _: 1
                      }, 8, ["items"])
                    ])
                  ]),
                  _createVNode(LevelDescriptionModal, {
                    isOpen: selectedLevel.value !== null,
                    level: selectedLevel.value || '',
                    thread: "grammar",
                    onClose: _cache[0] || (_cache[0] = ($event: any) => (selectedLevel.value = null))
                  }, null, 8, ["isOpen", "level"])
                ])
              ])
            ]))
    ])
  ], 64))
}
}

})